"use client";

import type { HrtDescriptionEntry } from "@/app/directories/shop/helper";
import { type ReactNode, createContext, useContext, useState } from "react";

export interface BaseHrt {
  hrtId: number;
  name: string;
  cash_prices?: {
    cashPrice: number | null;
  } | null;
  displayMetadata: {
    description?: HrtDescriptionEntry[];
    ingredients?: { value: string }[];
  };
  hrtPaymentType: {
    paymentType: "INSURED" | "CASH";
  }[];
  isConsumerFacing: boolean;
  ehrPrimaryAppointmentTypeId?: string[];
  isBookableOnStorefront?: boolean;
  popularity: number;
  HrtTranslation?: Array<{
    languageCode: string;
    name?: string;
    displayName?: string;
    displayMetadata?: {
      description?: Array<HrtDescriptionEntry>;
      ingredients?: Array<{ value: string }>;
    };
  }>;
}

export interface HrtOption {
  hrt: BaseHrt;
  product_component_type: "DEFAULT" | "INITIAL_REQUIRED";
  defaultHrt?: BaseHrt; // Reference to the DEFAULT option when this is INITIAL_REQUIRED
}

interface ProductContextType {
  selectedHrtId: number | undefined;
  setSelectedHrtId: (hrtId: number | undefined) => void;
  selectedOption: HrtOption | undefined;
  defaultOptions: HrtOption[];
  initialRequiredOptions: HrtOption[];
  selectedInitialRequiredHrtId: number | undefined;
  setSelectedInitialRequiredHrtId: (hrtId: number | undefined) => void;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export function ProductProvider({
  children,
  initialOptions,
  autoSelectDefault = true,
}: {
  children: ReactNode;
  initialOptions: HrtOption[];
  autoSelectDefault?: boolean;
}) {
  const defaultOptions = initialOptions
    .filter((opt) => opt.product_component_type === "DEFAULT")
    .sort((a, b) => b.hrt.popularity - a.hrt.popularity);
  const initialRequiredOptions = initialOptions.filter(
    (opt) => opt.product_component_type === "INITIAL_REQUIRED",
  );

  const [selectedHrtId, setSelectedHrtId] = useState<number | undefined>(
    autoSelectDefault ? defaultOptions[0]?.hrt.hrtId : undefined,
  );
  const [selectedInitialRequiredHrtId, setSelectedInitialRequiredHrtId] =
    useState<number | undefined>(
      initialRequiredOptions[0]?.hrt.hrtId ?? undefined,
    );

  const value = {
    selectedHrtId,
    setSelectedHrtId,
    selectedOption: defaultOptions.find(
      (opt) => opt.hrt.hrtId === selectedHrtId,
    ),
    defaultOptions,
    initialRequiredOptions,
    selectedInitialRequiredHrtId,
    setSelectedInitialRequiredHrtId,
  };

  return (
    <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
  );
}

export function useProduct() {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("useProduct must be used within a ProductProvider");
  }
  return context;
}
